import {injectable} from 'inversify';
import {CampaignTimerPanel, RibbonTheme} from "@/scenes.ui/campaign-timer-panel";
import TimeUtils from "@/utils/time-utils";
import {CampaignPanel} from "@/scenes.ui/campaign-panel";
import {
    AccessLevel,
    CampaignType,
    ChestType,
    InterfaceMode,
    Offer,
    OfferType,
    OkGroupCampaignDto,
    Player, StatusDto,
    WheelStatusDto
} from "@/store/player/types";
import {TabName} from "@/services/shop-service";
import PlayerStore from "@/store/player";
import SoundUtils from "@/utils/sound-utils";
import {CampaignRibbon} from "@/scenes.ui/campaign-ribbon";
import {Keys} from "@/constants/key";
import {InfoPanel} from "@/scenes.ui/info-panel";
import {FeedbackPanel} from "@/scenes.ui/feedback-panel";
import {TwoOptionsPanel} from "@/scenes.ui/two-options-panel";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import OnceCaller from "@/utils/once-caller";
import {ChestPanel} from "@/scenes.ui/chest-panel";
import {CampaignChestPanel} from "@/scenes.ui/campaign-chest-panel";
import {Tr, TrSpace} from "@/i18n/i18n";
import ListenerManager from "@/managers/listener-manager";
import SpriteUtils from "@/utils/sprite-utils";
import {CampaignWheelPanel} from "@/scenes.ui/campaign-wheel-panel";
import {CampaignWheelPanel2} from "@/scenes.ui/campaign-wheel-panel2";
import CU from "@/utils/c-u";
import Scene = Phaser.Scene;
import BaseSound = Phaser.Sound.BaseSound;
import Container = Phaser.GameObjects.Container;
import Sprite = Phaser.GameObjects.Sprite;
import Text = Phaser.GameObjects.Text;
import {CampaignArtifactPanel} from "@/scenes.ui/campaign-artifact-panel";
import {CampaignCommonPanel} from "@/scenes.ui/campaign-common-panel";

@injectable()
export class CampaignService {

    private scene: Scene | undefined;
    private canvas: HTMLCanvasElement | undefined;
    private offerTimerId: number | undefined;
    private campTimerId: number | undefined;

    private timerPanel: CampaignTimerPanel | undefined = undefined;
    private campaignPanel: CampaignPanel | undefined = undefined;

    private campaignRibbon: Container | undefined = undefined;

    private shopCallback!: (tabName: TabName) => unknown;

    private clickSound!: BaseSound | undefined;
    private completedSound!: BaseSound | undefined;

    private chestButton: Sprite | undefined;
    private chestTitle: Text | undefined;
    private chestCountBg: Sprite | undefined;
    private chestCount: Text | undefined;

    public initCampaignButtons(scene: Scene, canvas: HTMLCanvasElement, shopCallback: (tabName: TabName) => unknown, statClickCallback: (creatureId:string) => unknown,
                               clickSound: BaseSound | undefined, completedSound: BaseSound | undefined): void {
        this.scene = scene;
        this.canvas = canvas;
        this.shopCallback = shopCallback;

        this.clickSound = clickSound;
        this.completedSound = completedSound;

        let player = PlayerStore.getPlayer()!;
        let offer = player.offer;

        let self = this;

        let yy = 120;
        yy += (player.accessLevel === AccessLevel.TRIAL ? 50 : 0)

        if (!!offer && !!offer.offerName && (offer.offerType !== OfferType.MYTH_10 &&offer.offerType !== OfferType.MYTH_25 && offer.offerType !== OfferType.MYTH_50 && offer.offerType !== OfferType.MYTH_75 && offer.offerType !== OfferType.SUR_25)) {
            this.timerPanel = new CampaignTimerPanel(scene, 500, yy, RibbonTheme.GREEN, offer.offerName, true, () => this.openDiscountMenu(offer!));
            yy += 60;

            this.timerPanel.setTime(TimeUtils.getDurationText(CampaignService.remainSec()));

            this.scene!.add.existing(this.timerPanel);

            if (this.offerTimerId) {
                clearInterval(this.offerTimerId);
                this.offerTimerId = undefined;
            }

            this.offerTimerId = setInterval(() => {
                if (this.timerPanel && this.timerPanel.active && this.scene!.scene.isActive()) {
                    let remainSec = CampaignService.remainSec();
                    let durText = TimeUtils.getDurationText(remainSec);
                    this.timerPanel.setTime(durText);
                    if (this.campaignPanel) {
                        this.campaignPanel.setTime(durText);
                    }
                    if (remainSec <= 0) {
                        clearInterval(self.offerTimerId);
                        this.timerPanel.setVisible(false)
                        this.timerPanel.destroy();
                    }
                }
            }, 1000);

            if (!offer.viewed) {
                this.openDiscountMenu(offer);
            }
        }

        let campaignTypes: CampaignType[] = PlayerStore.getCampaignTypes();
        if (campaignTypes && campaignTypes.length > 0 && player.level > 2) {
            let campType = campaignTypes[0];

            if (campType === CampaignType.OK_GROUP) {
                this.campaignRibbon = new CampaignRibbon(scene, 510, yy, Tr('get.gift'), () => {
                    PlayerStore.fetchOkGroupCampaign().then((c: OkGroupCampaignDto) => {

                        if (!c.used) {
                            let ip: TwoOptionsPanel = new TwoOptionsPanel(scene, canvas, Tr('join.group'), Tr('join.group.official'),
                                Keys.ATLAS_PROFILE, Keys.UI_PROFILE_SOCIAL,
                                Tr('donate') + String(c.reward) + Tr('donate.all'),
                                c.member ? Tr('you.are.in.group') : Tr('join.group.donate'), () => {
                                    if (c.member) {
                                        PlayerStore.useCampaign(c.id).then(() => {
                                            new FeedbackPanel(scene, Tr('you.got.amethysts'), undefined, true).show();
                                            this.campaignRibbon!.setVisible(false);
                                        });
                                    } else {
                                        return window.open(c.groupUrl, '_blank');
                                    }
                                    ip.close();
                                }, c.member ? Tr('accept.donate') : Tr('in.group'));
                            ip.openAnimated();
                        } else {
                            let ip: InfoPanel = new InfoPanel(scene, canvas, Tr('oops'), Tr('you.used.action'),
                                Tr('with.us'), () => {});
                            ip.openAnimated();
                        }

                    });
                });
                this.scene.add.existing(this.campaignRibbon);
            } else if (campType === CampaignType.CHEST || campType === CampaignType.CHEST_2 || campType === CampaignType.CHEST_3) {
                let nm = Tr('c.chests');
                if (campType === CampaignType.CHEST_2) {
                    nm = Tr('c.chests2');
                } else if (campType === CampaignType.CHEST_3) {
                    nm = Tr('c.chests3');
                }
                this.campaignRibbon = new CampaignTimerPanel(scene, 505, yy, RibbonTheme.BLUE, nm, false, () => {

                    player = PlayerStore.getPlayer();
                    if (player.chestType === ChestType.GOLD || player.chestType === ChestType.SILVER
                            || player.chestType === ChestType.AZURE || player.chestType === ChestType.LAVA || player.chestType === ChestType.AMBER || player.chestType === ChestType.JADE) {
                        let chestPanel: ChestPanel = new ChestPanel(self.scene!, self.canvas!, player, self.clickSound, self.completedSound);
                        SoundUtils.play(self.clickSound);
                        chestPanel.openAnimated();
                    } else {
                        let ccp: CampaignChestPanel = new CampaignChestPanel(scene, canvas, clickSound, campType, () => {
                            SoundUtils.play(clickSound);
                            self.shopCallback(TabName.BANK);
                        });
                        SoundUtils.play(this.completedSound);
                        // SoundUtils.play(self.clickSound);
                        ccp.openAnimated();
                    }
                });
                this.scene.add.existing(this.campaignRibbon);
            } else if (campType === CampaignType.WHEEL) {
                this.campaignRibbon = new CampaignTimerPanel(scene, 505, yy, RibbonTheme.BLUE, Tr('c.wheel'), false, () => {
                    OnceCaller.doOnce("openWheelCampaign", () => {
                        player = PlayerStore.getPlayer();
                        if (PlayerStore.needRemindCampaignRules()){
                            PlayerStore.fetchWheelCampaignStatus().then((dto: WheelStatusDto) => {
                                let ccp: CampaignWheelPanel = new CampaignWheelPanel(scene, canvas, clickSound, () => {
                                    SoundUtils.play(clickSound);
                                    self.shopCallback(TabName.BANK);
                                }, dto);
                                SoundUtils.play(this.completedSound);
                                ccp.openAnimated();
                            });
                        } else {
                            PlayerStore.fetchWheelCampaignStatus().then((dto: WheelStatusDto) => {
                                let ccp: CampaignWheelPanel2 = new CampaignWheelPanel2(scene, canvas, clickSound, () => {
                                    SoundUtils.play(clickSound);
                                    self.shopCallback(TabName.BANK);
                                }, dto);
                                SoundUtils.play(this.completedSound);
                                ccp.openAnimated();
                            });
                        }
                    }, 2000);
                });
                this.scene.add.existing(this.campaignRibbon);
            } else if ([CampaignType.HERO, CampaignType.GEMS_PLUS_50, CampaignType.SNOW_WORLD, CampaignType.BOOSTER, CampaignType.PORTAL_DISCOUNT].includes(campType)) {

                if (player.stair > 30 || ![CampaignType.SNOW_WORLD, CampaignType.PORTAL_DISCOUNT].includes(campType)) {

                    this.campaignRibbon = new CampaignTimerPanel(scene, 505, yy, RibbonTheme.BLUE, this.getRibbonKey(campType), false, () => {
                        player = PlayerStore.getPlayer();

                        let ccp: CampaignCommonPanel = new CampaignCommonPanel(scene, canvas, campType, clickSound, () => {
                            SoundUtils.play(clickSound);
                            self.shopCallback(TabName.BANK);
                        }, statClickCallback);
                        SoundUtils.play(this.completedSound);
                        ccp.openAnimated();
                    });
                    this.scene.add.existing(this.campaignRibbon);

                }

            } else if (campType === CampaignType.ARTIFACT_SET_1) {
                this.campaignRibbon = new CampaignTimerPanel(scene, 505, yy, RibbonTheme.BLUE, Tr('art.d'), false, () => {
                    self.openArtifactsMenu();
                });
                this.scene.add.existing(this.campaignRibbon);
            }
        }

        this.updateChestButton();

        ListenerManager.setOpenChestPanelCallback(() => {
            if (self.scene && self.scene.scene.isActive()) {
                self.updateChestButton();

                // let chestPanel: ChestPanel = new ChestPanel(self.scene!, self.canvas!, PlayerStore.getPlayer());
                // chestPanel.openAnimated();
            }
        });
    }

    private getRibbonKey(campType: CampaignType): string {
        switch (campType) {
            case CampaignType.HERO:
                return Tr('c.hero');
            case CampaignType.GEMS_PLUS_50:
                return Tr('c.gems_plus_50');
            case CampaignType.SNOW_WORLD:
                return Tr('c.snow_world');
            case CampaignType.BOOSTER:
                return Tr('c.boosters');
            case CampaignType.PORTAL_DISCOUNT:
                return Tr('c.portal_discount');

        }
        return "";
    }

    public giveCampaignPanel(scene: Scene, canvas: HTMLCanvasElement, campaignType:CampaignType | undefined, shopCallback: (tabName: TabName) => unknown,
                             clickSound: BaseSound | undefined,):void {
        switch (campaignType){
            case CampaignType.WHEEL:
                PlayerStore.fetchWheelCampaignStatus().then((dto: WheelStatusDto) => {
                    let ccp: CampaignWheelPanel = new CampaignWheelPanel(scene, canvas, clickSound, () => {
                        SoundUtils.play(clickSound);
                        shopCallback(TabName.BANK);
                    }, dto);
                    // SoundUtils.play(this.completedSound);
                    ccp.openAnimated();
                });
                break;
            case CampaignType.CHEST:
            case CampaignType.CHEST_2:
            case CampaignType.CHEST_3:
                let ccp2: CampaignChestPanel = new CampaignChestPanel(scene, canvas, clickSound, campaignType, () => {
                    SoundUtils.play(clickSound);
                    shopCallback(TabName.BANK);
                });
                // SoundUtils.play(this.completedSound);
                ccp2.openAnimated();
                break;
        }
    }

    private static getButtonKey(chestType: ChestType): string {
        switch (chestType) {
            case ChestType.SILVER:
                return Keys.CHEST_SILVER_IC;
            case ChestType.GOLD:
                return Keys.CHEST_GOLDEN_IC;
            case ChestType.AZURE:
                return Keys.CHEST_AZURE_IC;
            case ChestType.LAVA:
                return Keys.CHEST_LAVA_IC;
            case ChestType.AMBER:
                return Keys.CHEST_AMBER_IC;
            case ChestType.JADE:
                return Keys.CHEST_JADE_IC;
        }
        return Keys.CHEST_SILVER_IC;
    }

    public updateChestButton() {
        let player: Player = PlayerStore.getPlayer();

        let self = this;
        if (player.chestType && player.chestType !== ChestType.NONE) {
            this.addChestButton(555, player.interfaceMode === InterfaceMode.ORIGINAL ? 280 : 420 + (this.campaignPanel && this.timerPanel? 60 : 0), CampaignService.getButtonKey(player.chestType), () => {
                let chestPanel: ChestPanel = new ChestPanel(self.scene!, self.canvas!, player, self.clickSound, self.completedSound);
                SoundUtils.play(self.clickSound);
                chestPanel.openAnimated();
            });
        }
    }

    private addChestButton(x: number, y: number, imageKey: string, callback: () => unknown): Sprite {
        this.destroyChestButton();
        this.chestButton = SpriteUtils.addSprite(this.scene!, x, y, imageKey);
        this.chestButton.setDepth(1000);
        this.chestTitle = TextUtils.addTextAdvanced(this.scene!, Tr('m.chest'), this.chestButton.x / CU.S, this.chestButton.y / CU.S + 45,
            '#ffffff', 9, 'normal', 90, 'center', 1, Fonts.ARIAL_MT);
        this.chestTitle.setStroke('#000000', 2);
        this.chestTitle.setDepth(1000);
        this.chestButton.setInteractive().on('pointerup', () => {
            OnceCaller.doOnce('chest_' + imageKey, callback);
        }, this.chestButton);


        let count = Math.min(PlayerStore.getPlayer().chestCount, 99);
        this.chestCountBg = SpriteUtils.addSprite(this.scene!, x+27, y+25, Keys.ATLAS_UI_FORCES, Keys.UI_FORCES_INDICATOR_BG);
        this.chestCountBg.setDepth(1000);
        this.chestCountBg.setVisible(count > 1)

        this.chestCount = TextUtils.addTextAdvanced(this.scene!, count.toString(), this.chestCountBg.x / CU.S, this.chestCountBg.y / CU.S+2,
            '#daba6f', 11, 'bold', 90, 'center', 1, Fonts.BENGALY_NORMAL);
        this.chestCount.setDepth(1000);
        this.chestCount.setVisible(count > 1)

        let self = this;
        ListenerManager.setCloseChestPanelCallback(() => {
            self.destroyChestButton();
            self.updateChestButton();
        });

        return this.chestButton;
    }

    private destroyChestButton() {
        if (this.chestButton) {
            this.chestButton.setVisible(false);
            this.chestButton.destroy();
            this.chestButton = undefined;
        }
        if (this.chestTitle) {
            this.chestTitle.setVisible(false);
            this.chestTitle.destroy();
            this.chestTitle = undefined;
        }
        if(this.chestCountBg){
            this.chestCountBg.setVisible(false);
            this.chestCountBg.destroy();
            this.chestCountBg = undefined;
        }
        if(this.chestCount){
            this.chestCount.setVisible(false);
            this.chestCount.destroy();
            this.chestCount = undefined;
        }
    }

    private static remainSec(): number {
        let player = PlayerStore.getPlayer()!;
        let offer = player.offer;
        if (!!player.offer && !!player.offer.offerType) {
            const remainSec = Math.floor((offer!.validTill - TimeUtils.getServerTimeMs()) / 1000);
            return Math.max(remainSec, 0);
        }
        return 0;
    }

    private openArtifactsMenu() {
        let self = this;
        let cp: CampaignArtifactPanel | undefined = new CampaignArtifactPanel(this.scene!, this.canvas!, () => {
            SoundUtils.play(self.clickSound);
            self.shopCallback(TabName.ARTIFACT);
            cp = undefined;
        });

        SoundUtils.play(this.completedSound);
        cp.openAnimated();
        PlayerStore.setOfferSeen();
    }

    private openDiscountMenu(offer: Offer) {
        let self = this;
        this.campaignPanel = new CampaignPanel(this.scene!, this.canvas!, offer, () => {
            SoundUtils.play(self.clickSound);
            self.shopCallback(CampaignService.getShopTab(offer));
            self.campaignPanel = undefined;
        }, () => {
            SoundUtils.play(self.clickSound);
            self.campaignPanel = undefined;
        });

        let remainSec = CampaignService.remainSec();
        let durText = TimeUtils.getDurationText(remainSec);
        if (this.campaignPanel && this.campaignPanel.active) {
            this.campaignPanel.setTime(durText);
        }

        SoundUtils.play(this.completedSound);
        this.campaignPanel.openAnimated();
        PlayerStore.setOfferSeen();
    }

    private static getShopTab(offer: Offer): TabName {
        switch (offer.offerType) {
            case OfferType.FIRST_HERO:
            case OfferType.ALL_CREATURES_DISCOUNT:
            case OfferType.GREEN_DRAGON_DISCOUNT:
            case OfferType.BLACK_DRAGON_DISCOUNT:
            case OfferType.BRAND_DISCOUNT:
                return TabName.HERO;
        }
        return TabName.BANK;
    }
}
